import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  TableSortLabel,
  TablePagination,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  IconButton,
  TableFooter,
  Grid,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
} from '@mui/material';
import { format, parseISO, isBefore, startOfDay } from 'date-fns';
import apiService from '../../services/apiService';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import PrintIcon from '@mui/icons-material/Print';
import BillPrintDialog from './BillPrintDialog';
import CustomerHistoryDialog from './CustomerHistoryDialog';

// Custom hook for debouncing
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

const CustomerLedger = () => {
  const [ledgerData, setLedgerData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [orderBy, setOrderBy] = useState('customerName');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [grandTotalBalance, setGrandTotalBalance] = useState(0);
  const [grandTotalBillAmount, setGrandTotalBillAmount] = useState(0);
  const [grandTotalReceivedAmount, setGrandTotalReceivedAmount] = useState(0);
  const [isPrintListDialogOpen, setPrintListDialogOpen] = useState(false);
  const [isBillPrintDialogOpen, setBillPrintDialogOpen] = useState(false);
  const [isHistoryDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedBillDetails, setSelectedBillDetails] = useState([]);
  const [selectedCustomerHistory, setSelectedCustomerHistory] = useState([]);
  const [searchName, setSearchName] = useState('');
  const [minBalance, setMinBalance] = useState(1);

  // State for history dialog pagination
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);
  const [historyTotalCount, setHistoryTotalCount] = useState(0);

  const debouncedSearchName = useDebounce(searchName, 300);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handlePrintList = () => {
    window.print();
  };

  const fetchLedgerData = useCallback(async (forceMode = null) => {
    try {
      const date = new Date(selectedDate);
      const today = new Date();
      
      const params = {
        endDate: selectedDate,
        minBalance: minBalance,
        sortBy: orderBy,
        direction: order,
        page,
        size: rowsPerPage,
      };

      // If mode is forced to daily, or the selected date is before today, trigger daily report mode
      if (forceMode === 'daily' || isBefore(startOfDay(date), startOfDay(today))) {
        params.startDate = selectedDate;
      }
      
      if (debouncedSearchName.length >= 3) {
        params.customerName = debouncedSearchName;
      }

      const response = await apiService.collections.getCustomerLedger(params);

      // Correctly access the nested 'page' object from the response
      const pageData = response.data?.page;
      const content = pageData?.content || [];
      const total = pageData?.totalElements ?? 0;

      setLedgerData(Array.isArray(content) ? content : []);
      setTotalCount(total);
      setGrandTotalBalance(response.data.grandTotalBalance ?? 0);
      setGrandTotalBillAmount(response.data.grandTotalBillAmount ?? 0);
      setGrandTotalReceivedAmount(response.data.grandTotalReceivedAmount ?? 0);
    } catch (error) {
      console.error('Error fetching customer ledger:', error);
      setLedgerData([]);
      setTotalCount(0);
      setGrandTotalBalance(0);
      setGrandTotalBillAmount(0);
      setGrandTotalReceivedAmount(0);
    }
  }, [selectedDate, orderBy, order, page, rowsPerPage, debouncedSearchName, minBalance]);

  useEffect(() => {
    fetchLedgerData();
  }, [fetchLedgerData]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleIndividualPrint = async (bill) => {
    try {
      const response = await apiService.salesDetails.getSalesByBillSummary(bill.billSummaryId);
      setSelectedBill(bill);
      setSelectedBillDetails(response.data);
      setBillPrintDialogOpen(true);
    } catch (error) {
      console.error('Error fetching sales details for printing:', error);
      alert('Could not fetch bill details for printing.');
    }
  };

  const fetchCustomerHistory = async (customerId, historyPage, historyRowsPerPage) => {
    try {
      const response = await apiService.collections.getCustomerRecentBills(customerId, {
        page: historyPage,
        size: historyRowsPerPage,
      });
      const history = response.data.content || response.data;
      setSelectedCustomerHistory(Array.isArray(history) ? history : []);
      setHistoryTotalCount(response.data.totalElements ?? 0);
    } catch (error) {
      console.error('Error fetching customer history:', error);
      alert('Could not fetch customer history.');
    }
  };

  const handleHistoryClick = async (customer) => {
    setSelectedBill(customer);
    setHistoryPage(0); // Reset to first page
    await fetchCustomerHistory(customer.customerId, 0, historyRowsPerPage);
    setHistoryDialogOpen(true);
  };

  const handleHistoryPageChange = (newPage) => {
    setHistoryPage(newPage);
    fetchCustomerHistory(selectedBill.customerId, newPage, historyRowsPerPage);
  };

  const handleHistoryRowsPerPageChange = (newRowsPerPage) => {
    setHistoryRowsPerPage(newRowsPerPage);
    setHistoryPage(0);
    fetchCustomerHistory(selectedBill.customerId, 0, newRowsPerPage);
  };

  const handleTodaysReportClick = () => {
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    setSelectedDate(todayStr);
    // Explicitly call fetchLedgerData with 'daily' mode
    fetchLedgerData('daily');
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, flexGrow: 1, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 24px)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant={isMobile ? "h6" : "h4"} gutterBottom={!isMobile} sx={{ fontWeight: isMobile ? 800 : 400, mb: 0 }}>
            Customer Ledger
          </Typography>
          {isMobile && (
            <Button 
              variant="outlined" 
              onClick={handleTodaysReportClick} 
              size="small" 
              sx={{ padding: '2px 8px', minWidth: 'auto', fontSize: '0.75rem', height: 30 }}
            >
              Today
            </Button>
          )}
        </Box>
        {isMobile && (
          <IconButton onClick={() => setPrintListDialogOpen(true)} color="secondary" size="small">
            <PrintIcon />
          </IconButton>
        )}
      </Box>

      <Paper sx={{ p: { xs: 1, md: 2 }, mb: 2 }}>
        {isMobile ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              label="Date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: '110px', '& .MuiInputBase-input': { fontSize: '0.8rem', padding: '8px 4px' } }}
            />
            <TextField
              label="Min"
              type="number"
              value={minBalance}
              onChange={(e) => setMinBalance(e.target.value)}
              size="small"
              sx={{ width: '60px', '& .MuiInputBase-input': { fontSize: '0.8rem', padding: '8px 4px' } }}
            />
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              fullWidth
              sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.8rem', padding: '8px 4px' } }}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <TextField
              label="As of Date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ maxWidth: '200px' }}
            />
            <TextField
              label="Min Balance"
              type="number"
              value={minBalance}
              onChange={(e) => setMinBalance(e.target.value)}
              size="small"
              sx={{ maxWidth: '100px' }}
            />
            <Button variant="outlined" onClick={handleTodaysReportClick}>Today's Report</Button>
            <TextField
              label="Search by Name"
              variant="outlined"
              size="small"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              sx={{ flex: 1, maxWidth: '400px' }}
            />
            <Button variant="contained" color="secondary" onClick={() => setPrintListDialogOpen(true)}>
              Print List
            </Button>
          </Box>
        )}
      </Paper>

      {isMobile ? (
        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, mb: 1 }}>
          {ledgerData.map((bill) => (
            <Card key={bill.billSummaryId} sx={{ mb: 1, boxShadow: 2 }}>
              <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem', flex: 1, mr: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {bill.customerName} <Typography component="span" variant="caption" color="text.secondary">- {bill.customerCity} - #{bill.manualBillNo}</Typography>
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>{format(parseISO(bill.billDate), 'dd/MM/yyyy')}</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: '#f5f5f5', p: 1, borderRadius: 1, mb: 1.5 }}>
                  <Box>
                    <Typography variant="caption" display="block" color="text.secondary">Bill Amt</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{(Number(bill.billAmount) || 0).toFixed(2)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" display="block" color="text.secondary">Received</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>{(Number(bill.receivedAmount) || 0).toFixed(2)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" display="block" color="text.secondary">Balance</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>{(Number(bill.cumulativeBalance) || 0).toFixed(2)}</Typography>
                  </Box>
                </Box>

                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  startIcon={<HistoryIcon />}
                  onClick={() => handleHistoryClick(bill)}
                >
                  History
                </Button>
              </CardContent>
            </Card>
          ))}
          {ledgerData.length === 0 && (
             <Typography variant="body2" align="center" sx={{ mt: 4, color: 'text.secondary' }}>No records found</Typography>
          )}
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>S.No</TableCell>
              <TableCell>Bill No</TableCell>
              <TableCell>
                <TableSortLabel active={orderBy === 'customerName'} direction={orderBy === 'customerName' ? order : 'asc'} onClick={() => handleSort('customerName')}>
                  Customer Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={orderBy === 'customerCity'} direction={orderBy === 'customerCity' ? order : 'asc'} onClick={() => handleSort('customerCity')}>
                  City
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={orderBy === 'billDate'} direction={orderBy === 'billDate' ? order : 'asc'} onClick={() => handleSort('billDate')}>
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel active={orderBy === 'billAmount'} direction={orderBy === 'billAmount' ? order : 'asc'} onClick={() => handleSort('billAmount')}>
                  Bill Amount
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Received Amount</TableCell>
              <TableCell align="right">
                <TableSortLabel active={orderBy === 'balanceAmount'} direction={orderBy === 'balanceAmount' ? order : 'asc'} onClick={() => handleSort('balanceAmount')}>
                  Balance Amount
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ledgerData.map((bill, index) => (
              <TableRow key={bill.billSummaryId}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>{bill.manualBillNo}</TableCell>
                <TableCell>{bill.customerName}</TableCell>
                <TableCell>{bill.customerCity}</TableCell>
                <TableCell>{format(parseISO(bill.billDate), 'dd/MM/yyyy')}</TableCell>
                <TableCell align="right">{(Number(bill.billAmount) || 0).toFixed(2)}</TableCell>
                <TableCell align="right">{(Number(bill.receivedAmount) || 0).toFixed(2)}</TableCell>
                <TableCell align="right">{(Number(bill.cumulativeBalance) || 0).toFixed(2)}</TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleHistoryClick(bill)}
                  >
                    History
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter sx={{ position: 'sticky', bottom: 0, zIndex: 1, backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Totals:</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{grandTotalBillAmount.toFixed(2)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{grandTotalReceivedAmount.toFixed(2)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{grandTotalBalance.toFixed(2)}</TableCell>
              <TableCell />
            </TableRow>
          </TableFooter>
        </Table>
        </TableContainer>
      )}

      {isMobile && (
        <Paper sx={{ p: 1.5, mb: 1, bgcolor: '#e3f2fd' }}>
           <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box>
                 <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>Total Bill</Typography>
                 <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{grandTotalBillAmount.toFixed(2)}</Typography>
              </Box>
              <Box>
                 <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>Total Rcvd</Typography>
                 <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.main' }}>{grandTotalReceivedAmount.toFixed(2)}</Typography>
              </Box>
              <Box>
                 <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>Total Bal</Typography>
                 <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'error.main' }}>{grandTotalBalance.toFixed(2)}</Typography>
              </Box>
           </Box>
        </Paper>
      )}

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />

      <Dialog
        open={isPrintListDialogOpen}
        onClose={() => setPrintListDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        className="print-list-dialog"
      >
        <DialogTitle sx={{ '@media print': { display: 'none' } }}>
          Customer Ledger
          <IconButton
            aria-label="close"
            onClick={() => setPrintListDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <style>
            {`
              @media print {
                body > *:not(.print-list-dialog) {
                  display: none !important;
                }
                .print-list-dialog .MuiDialog-paper {
                  margin: 0 !important;
                  max-width: 100% !important;
                  width: 100% !important;
                  height: 100% !important;
                  max-height: 100% !important;
                  box-shadow: none !important;
                  border-radius: 0 !important;
                }
              }
            `}
          </style>
          <Box id="customer-ledger-print-area" sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Customer Ledger as of {format(parseISO(selectedDate), 'dd/MM/yyyy')}
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>S.No</TableCell>
                    <TableCell>Bill No</TableCell>
                    <TableCell>Customer Name</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Bill Amount</TableCell>
                    <TableCell align="right">Received Amount</TableCell>
                    <TableCell align="right">Balance Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ledgerData.map((bill, index) => (
                    <TableRow key={bill.billSummaryId}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{bill.manualBillNo}</TableCell>
                      <TableCell>{bill.customerName}</TableCell>
                      <TableCell>{bill.customerCity}</TableCell>
                      <TableCell>{format(parseISO(bill.billDate), 'dd/MM/yyyy')}</TableCell>
                      <TableCell align="right">{(Number(bill.billAmount) || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">{(Number(bill.receivedAmount) || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">{(Number(bill.cumulativeBalance) || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold' }}>Totals:</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{grandTotalBillAmount.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{grandTotalReceivedAmount.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{grandTotalBalance.toFixed(2)}</TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions sx={{ '@media print': { display: 'none' } }}>
          <Button onClick={() => setPrintListDialogOpen(false)}>Close</Button>
          <Button onClick={handlePrintList} variant="contained" startIcon={<PrintIcon />}>
            Print
          </Button>
        </DialogActions>
      </Dialog>

      {selectedBill && (
        <CustomerHistoryDialog
          open={isHistoryDialogOpen}
          onClose={() => setHistoryDialogOpen(false)}
          customer={selectedBill}
          historyData={selectedCustomerHistory}
          onPrintBill={handleIndividualPrint}
          page={historyPage}
          rowsPerPage={historyRowsPerPage}
          totalCount={historyTotalCount}
          onPageChange={handleHistoryPageChange}
          onRowsPerPageChange={handleHistoryRowsPerPageChange}
        />
      )}

      {selectedBill && (
        <BillPrintDialog
          open={isBillPrintDialogOpen}
          onClose={() => setBillPrintDialogOpen(false)}
          bill={selectedBill}
          salesDetails={selectedBillDetails}
        />
      )}
    </Box>
  );
};

export default CustomerLedger;
